// Pure helpers for clinic visibility per UI surface (Settings → Clinic visibility;
// GET/PUT /api/settings/clinic-visibility, and `hiddenOn` on every /api/tenants/tenants item).
// "Clinics" are the tenant rows — including Plato-conversion rows such as "Healo Technical" —
// and a surface is a place in the UI where clinics are listed. Only the HIDDEN pairs are
// stored, so a clinic opened later is visible everywhere by default.
//
// Two surfaces exist today: `work_plan` is filtered SERVER-side (the day bundle never lists a
// hidden clinic) and `calendar` is enforced CLIENT-side via filterClinicsForSurface on the
// Calendar / Slots / Sessions / Time-off pages (and the phone's Day/Sessions tabs). This is
// distinct from the access-group "hidden clinics" (WhatsApp bot ids on the Messages tabs) and
// from the planner's per-device "Clinics" control, which is a local layer on top.

/** Fallback surface vocabulary when the server sends none (older backend or a partial payload). */
export const CLINIC_SURFACES = Object.freeze([
  Object.freeze({ key: "work_plan", label: "Work Plan" }),
  Object.freeze({ key: "calendar", label: "Calendar views" }),
]);

function cleanSurfaceList(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const value of raw) {
    const key = String(value || "").trim().toLowerCase();
    if (key && !out.includes(key)) out.push(key);
  }
  return out;
}

/** The surfaces a tenant item is hidden on (`hiddenOn`); a missing field means visible everywhere. */
export function hiddenSurfacesOf(tenant) {
  return cleanSurfaceList(tenant?.hiddenOn);
}

export function isClinicHiddenOn(tenant, surface) {
  const key = String(surface || "").trim().toLowerCase();
  return Boolean(key) && hiddenSurfacesOf(tenant).includes(key);
}

/**
 * Tenant items minus those hidden on `surface` — the one-liner the calendar views drop in
 * after fetchTenants. A non-array (older/failed response) reads as no clinics, matching the
 * `Array.isArray(response?.items) ? … : []` idiom those pages already use.
 */
export function filterClinicsForSurface(items, surface) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => !isClinicHiddenOn(item, surface));
}

/** { tenantId: [surfaces] } from the editor's clinic rows — only non-empty entries. */
export function hiddenMapFromClinics(clinics) {
  const map = {};
  for (const clinic of Array.isArray(clinics) ? clinics : []) {
    const id = String(clinic?.id || "").trim();
    const hidden = hiddenSurfacesOf(clinic);
    if (id && hidden.length > 0) map[id] = hidden;
  }
  return map;
}

/** Next hidden map after flipping one clinic on one surface; empty entries are dropped. */
export function toggleSurfaceHidden(hiddenMap, clinicId, surface) {
  const id = String(clinicId || "").trim();
  const key = String(surface || "").trim().toLowerCase();
  const next = {};
  for (const [tenantId, surfaces] of Object.entries(hiddenMap || {})) {
    const list = cleanSurfaceList(surfaces);
    if (list.length > 0) next[tenantId] = list;
  }
  if (!id || !key) return next;
  const current = next[id] || [];
  const updated = current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key];
  if (updated.length > 0) next[id] = updated;
  else delete next[id];
  return next;
}

/** The PUT body: { hidden: { tenantId: [surfaces] } } with blanks and empty lists removed. */
export function hiddenMapToBody(hiddenMap) {
  const hidden = {};
  for (const [tenantId, surfaces] of Object.entries(hiddenMap || {})) {
    const id = String(tenantId || "").trim();
    const list = cleanSurfaceList(surfaces);
    if (id && list.length > 0) hidden[id] = list;
  }
  return { hidden };
}

/** Same hidden pairs, regardless of list order — the editor's dirty check. */
export function hiddenMapsEqual(a, b) {
  const left = hiddenMapToBody(a).hidden;
  const right = hiddenMapToBody(b).hidden;
  const leftIds = Object.keys(left).sort();
  const rightIds = Object.keys(right).sort();
  if (leftIds.length !== rightIds.length || leftIds.some((id, index) => id !== rightIds[index])) return false;
  return leftIds.every((id) => {
    const l = [...left[id]].sort();
    const r = [...right[id]].sort();
    return l.length === r.length && l.every((key, index) => key === r[index]);
  });
}

function normalizeSurfaces(raw) {
  if (!Array.isArray(raw)) return CLINIC_SURFACES;
  const out = [];
  const seen = new Set();
  for (const item of raw) {
    const key = String(item?.key || "").trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({ key, label: String(item?.label || "").trim() || key });
  }
  return out.length > 0 ? out : CLINIC_SURFACES;
}

/**
 * GET/PUT /api/settings/clinic-visibility → { clinics: [{id, name, shortName, address,
 * hiddenOn: []}], surfaces: [{key, label}] (fallback CLINIC_SURFACES), canEdit, raw }.
 */
export function normalizeClinicVisibilityPayload(payload) {
  const clinics = [];
  for (const item of Array.isArray(payload?.clinics) ? payload.clinics : []) {
    const id = String(item?.id || "").trim();
    if (!id) continue;
    clinics.push({
      id,
      name: String(item?.name || "").trim() || id,
      shortName: String(item?.shortName || "").trim(),
      address: String(item?.address || "").trim(),
      hiddenOn: hiddenSurfacesOf(item),
    });
  }
  return {
    clinics,
    surfaces: normalizeSurfaces(payload?.surfaces),
    canEdit: Boolean(payload?.canEdit),
    raw: payload,
  };
}
